import React, { useState, useEffect } from 'react';
import { SidebarPortal, BlockDataForm } from '@plone/volto/components';
import mediaPlayerSchema from './schema';
import './mediaplayer.css';

const Edit = (props) => {
  const { data, block, onChangeBlock, selected } = props;
  const [playerType, setPlayerType] = useState(data.playerType || 'auto');

  // Get media URL from the selected file
  const getMediaUrl = (file) => {
    if (!file) return '';

    // Handle different possible structures
    let fileObj = file;
    if (Array.isArray(file)) {
      fileObj = file[0];
    }

    if (!fileObj || typeof fileObj !== 'object') return '';

    // Get the base URL and filename
    let url = fileObj['@id'] || fileObj.url || fileObj.href || '';
    let filename = fileObj.title || fileObj.filename || '';

    if (!url) return '';

    // If URL is relative, make it absolute
    if (url.startsWith('/')) {
      url = `${window.location.origin}${url}`;
    }

    // Construct the proper Plone download URL with filename
    let finalUrl = url;
    if (url && !url.includes('@@download')) {
      if (filename) {
        finalUrl = `${url}/@@download/file/${filename}`;
      } else {
        finalUrl = `${url}/@@download/file`;
      }
    }

    return finalUrl;
  };

  // Determine if file is audio or video based on content type or filename
  const detectMediaType = (file) => {
    if (!file) return 'video'; // default

    const fileObj = Array.isArray(file) ? file[0] : file;
    if (!fileObj) return 'video';

    const contentType = fileObj['content-type'] || fileObj.mime_type || '';
    const filename = fileObj.title || fileObj.filename || '';

    // Check content type first
    if (contentType.startsWith('audio/')) return 'audio';
    if (contentType.startsWith('video/')) return 'video';

    // Fallback to filename extension
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'];
    const videoExtensions = ['.mp4', '.webm', '.ogv', '.mov', '.avi', '.mkv'];

    const lowerFilename = filename.toLowerCase();

    if (audioExtensions.some((ext) => lowerFilename.endsWith(ext)))
      return 'audio';
    if (videoExtensions.some((ext) => lowerFilename.endsWith(ext)))
      return 'video';

    return 'video'; // default
  };

  // Update player type when file changes and type is set to auto
  useEffect(() => {
    if (data.file && (data.playerType === 'auto' || !data.playerType)) {
      const detectedType = detectMediaType(data.file);
      setPlayerType(detectedType);
    } else if (data.playerType && data.playerType !== 'auto') {
      setPlayerType(data.playerType);
    }
  }, [data.file, data.playerType]);

  const mediaUrl = getMediaUrl(data.file);
  const currentPlayerType =
    data.playerType === 'auto' ? playerType : data.playerType || 'video';

  // Get the width class for the block
  const getWidthClass = (width) => {
    switch (width) {
      case 'narrow':
        return 'narrow-width';
      case 'wide':
        return 'wide-width';
      default:
        return 'default-width';
    }
  };

  const widthClass = getWidthClass(data.blockWidth);

  const handleFieldChange = (id, value) => {
    if (id === 'file') {
      // Make sure the file is stored correctly
      const fixedFile = Array.isArray(value) ? value : [value];
      onChangeBlock(block, { ...data, [id]: fixedFile });
    } else {
      onChangeBlock(block, { ...data, [id]: value });
    }
  };

  // Generate the schema with proper props
  const schema = mediaPlayerSchema(props);

  // Prepare form data for the sidebar
  const formData = {
    ...data,
    file: Array.isArray(data.file) ? data.file : data.file ? [data.file] : [],
  };

  return (
    <>
      <div className={`media-player-block ${widthClass}`}>
        {!mediaUrl ? (
          <div className="media-player-placeholder">
            <div className="placeholder-content">
              <div className="placeholder-icon">
                {currentPlayerType === 'audio' ? '🎵' : '🎬'}
              </div>
              <p>Select a {currentPlayerType} file to display the player</p>
            </div>
          </div>
        ) : (
          <div className="media-player-container">
            {data.title && <h3 className="media-player-title">{data.title}</h3>}

            {currentPlayerType === 'video' ? (
              <video
                src={mediaUrl}
                controls={data.controls !== false}
                autoPlay={data.autoplay || false}
                loop={data.loop || false}
                muted={data.muted || false}
                preload={data.preload || 'metadata'}
                width={data.width || '100%'}
                height={data.height || '400px'}
                style={{
                  borderRadius: data.borderRadius || '8px',
                  maxWidth: '100%',
                }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <audio
                src={mediaUrl}
                controls={data.controls !== false}
                autoPlay={data.autoplay || false}
                loop={data.loop || false}
                muted={data.muted || false}
                preload={data.preload || 'metadata'}
                style={{
                  width: data.width || '100%',
                  borderRadius: data.borderRadius || '8px',
                }}
              >
                Your browser does not support the audio tag.
              </audio>
            )}

            {data.caption && (
              <div className="media-player-caption">
                <p>{data.caption}</p>
              </div>
            )}

            {data.description && (
              <div className="media-player-description">
                <p>{data.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {selected && (
        <SidebarPortal selected={selected}>
          <BlockDataForm
            schema={schema}
            title="Media Player"
            onChangeField={handleFieldChange}
            onChangeBlock={onChangeBlock}
            block={block}
            formData={formData}
          />
        </SidebarPortal>
      )}
    </>
  );
};

export default Edit;
